import {
  COMPLIANCE_LOG_OUTCOMES,
  COMPLIANCE_LOG_OUTCOME_LABELS,
  type ComplianceLogOutcome,
  type SubmitComplianceLogInput,
} from '@sitewise/shared';
import {
  Button,
  Input,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
} from '@sitewise/ui';
import { useMutation } from '@tanstack/react-query';
import { FileUp, X } from 'lucide-react';
import { useId, useRef, useState, type FormEvent } from 'react';

import {
  confirmLogAttachment,
  presignLogAttachment,
  submitComplianceLog,
  type RecordWithDeviceType,
} from '../../../api/compliance.js';
import { ApiError } from '../../../lib/api-client.js';

import { formatDate } from './lib.js';

interface Props {
  record: RecordWithDeviceType;
  onClose: () => void;
  onSubmitted: () => void;
}

interface FormState {
  performedOn: string;
  outcome: ComplianceLogOutcome;
  notes: string;
}

interface PendingFile {
  id: string;
  file: File;
  attachmentId?: string;
  state: 'uploading' | 'uploaded' | 'error';
  error?: string;
}

function todayIso(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function pickKind(file: File): 'compliance_log_photo' | 'compliance_log_document' {
  return file.type.startsWith('image/') ? 'compliance_log_photo' : 'compliance_log_document';
}

export function LogComplianceSheet({ record, onClose, onSubmitted }: Props) {
  const performedId = useId();
  const outcomeId = useId();
  const notesId = useId();
  const fileRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<FormState>({
    performedOn: todayIso(),
    outcome: 'passed',
    notes: '',
  });
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: async () => {
      const attachmentIds = files
        .filter((f) => f.state === 'uploaded' && f.attachmentId)
        .map((f) => f.attachmentId!);
      const input: SubmitComplianceLogInput = {
        performedOn: form.performedOn,
        outcome: form.outcome,
        notes: form.notes.trim() || null,
        attachmentIds,
        signatureAttachmentId: null,
        nextDueOverride: null,
        loggedByVendorUserId: null,
        metadata: {},
      };
      return submitComplianceLog(record.id, input);
    },
    onSuccess: (result) => {
      toast.success(
        result.followUpWorkOrderId
          ? 'Log saved — follow-up work order created.'
          : 'Log saved.',
      );
      onSubmitted();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Could not save log');
    },
  });

  async function uploadFile(file: File) {
    const localId = `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`;
    setFiles((prev) => [...prev, { id: localId, file, state: 'uploading' }]);
    try {
      const kind = pickKind(file);
      const presigned = await presignLogAttachment(record.id, {
        kind,
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
      });
      const put = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: presigned.headers,
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      await confirmLogAttachment(record.id, presigned.attachment.id);
      setFiles((prev) =>
        prev.map((f) =>
          f.id === localId
            ? { ...f, state: 'uploaded', attachmentId: presigned.attachment.id }
            : f,
        ),
      );
    } catch (err) {
      setFiles((prev) =>
        prev.map((f) =>
          f.id === localId
            ? {
                ...f,
                state: 'error',
                error: err instanceof Error ? err.message : 'Upload failed',
              }
            : f,
        ),
      );
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (files.some((f) => f.state === 'uploading')) {
      setError('Wait for uploads to finish');
      return;
    }
    submit.mutate();
  }

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="flex flex-col overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Log inspection</SheetTitle>
          <SheetDescription>
            {record.device.label} · {record.deviceType.name} ·{' '}
            <span className="tabular-nums">due {formatDate(record.dueAt)}</span>
          </SheetDescription>
        </SheetHeader>

        <form className="flex flex-1 flex-col gap-4" onSubmit={handleSubmit}>
          <div className="grid gap-1.5">
            <Label htmlFor={performedId}>Performed on</Label>
            <Input
              id={performedId}
              type="date"
              required
              max={todayIso()}
              value={form.performedOn}
              onChange={(e) => setForm({ ...form, performedOn: e.target.value })}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor={outcomeId}>Outcome</Label>
            <select
              id={outcomeId}
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
              value={form.outcome}
              onChange={(e) =>
                setForm({ ...form, outcome: e.target.value as ComplianceLogOutcome })
              }
            >
              {COMPLIANCE_LOG_OUTCOMES.map((o) => (
                <option key={o} value={o}>
                  {COMPLIANCE_LOG_OUTCOME_LABELS[o]}
                </option>
              ))}
            </select>
            {form.outcome === 'failed' || form.outcome === 'requires_followup' ? (
              <p className="text-xs text-fg-muted">
                A follow-up work order will be created automatically.
              </p>
            ) : null}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor={notesId}>Notes</Label>
            <textarea
              id={notesId}
              rows={4}
              maxLength={8_000}
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Findings, follow-up items, certificate numbers…"
            />
          </div>

          <div className="grid gap-1.5">
            <Label>Photos / certificates</Label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,.pdf,application/pdf"
              className="hidden"
              multiple
              onChange={(e) => {
                const list = e.target.files;
                if (!list) return;
                for (const file of Array.from(list)) void uploadFile(file);
                e.target.value = '';
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => fileRef.current?.click()}
            >
              <FileUp className="h-3.5 w-3.5" />
              Upload file
            </Button>
            {files.length > 0 ? (
              <ul className="space-y-1 text-sm">
                {files.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-center justify-between gap-2 rounded border border-border px-2 py-1"
                  >
                    <span className="truncate">
                      {f.file.name}{' '}
                      <span className="text-xs text-fg-muted">
                        {f.state === 'uploading'
                          ? 'Uploading…'
                          : f.state === 'error'
                            ? f.error
                            : 'Ready'}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="text-fg-muted hover:text-danger"
                      onClick={() => setFiles((prev) => prev.filter((x) => x.id !== f.id))}
                      aria-label="Remove file"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-fg-muted">
                Optional. Attach the inspection PDF or photos before saving.
              </p>
            )}
          </div>

          {error ? (
            <div className="rounded border border-danger-subtle bg-danger-subtle/40 px-3 py-2 text-sm text-danger">
              {error}
            </div>
          ) : null}

          <SheetFooter className="mt-auto">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submit.isPending}>
              {submit.isPending ? 'Saving…' : 'Save log'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
